import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

function dateOf(value: string | null): Date {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value ?? '');
  const date = new Date();
  date.setDate(1);
  if (match) date.setFullYear(Number(match[1]), Number(match[2]) - 1, 1);
  return date;
}

function valueOf(date: Date): string {
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** A disclosure with native buttons: Tab visits every month; Escape restores trigger focus. */
export function MonthPicker({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => dateOf(value).getFullYear());
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const date = dateOf(value);
  const language = i18n.resolvedLanguage ?? 'it';
  const fullMonth = new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' });
  const monthName = new Intl.DateTimeFormat(language, { month: 'long' });
  const all = value === null || value === 'all';
  function close() {
    setOpen(false);
    trigger.current?.focus();
  }
  useEffect(() => {
    if (!open) return;
    function outside(event: PointerEvent) {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  function step(delta: number) {
    const next = dateOf(value);
    next.setMonth(next.getMonth() + delta);
    setYear(next.getFullYear());
    onChange(valueOf(next));
  }
  return (
    <div
      ref={root}
      role="group"
      aria-label={label}
      className="relative flex max-w-full items-center"
      onBlur={(event) => {
        // Safari and macOS Firefox don't focus a clicked button, so relatedTarget is null:
        // closing then would drop the click. Outside clicks are left to the pointerdown handler.
        const next = event.relatedTarget;
        if (next !== null && !event.currentTarget.contains(next)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          close();
        }
      }}
    >
      <Button
        type="button"
        variant="ghost"
        className="min-h-11 min-w-11 p-2"
        aria-label={t('monthPicker.previousMonth')}
        disabled={date.getFullYear() <= 1 && date.getMonth() === 0}
        onClick={() => step(-1)}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <Button
        ref={trigger}
        type="button"
        variant="outline"
        className="h-auto min-h-11 min-w-11 whitespace-normal"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setYear(date.getFullYear());
          setOpen(!open);
        }}
      >
        {label}: {all ? t('monthPicker.allPeriods') : fullMonth.format(date)}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="min-h-11 min-w-11 p-2"
        aria-label={t('monthPicker.nextMonth')}
        disabled={date.getFullYear() >= 9999 && date.getMonth() === 11}
        onClick={() => step(1)}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
      {open ? (
        <div
          id={panelId}
          role="group"
          aria-label={`${label} ${year}`}
          className="bg-popover text-popover-foreground border-border absolute top-full left-0 z-50 mt-1 w-[min(18rem,calc(100vw-2rem))] rounded-xl border p-2 shadow-lg"
        >
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              className="min-h-11 min-w-11 p-2"
              aria-label={t('monthPicker.previousYear')}
              disabled={year <= 1}
              onClick={() => setYear(year - 1)}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <span aria-live="polite" className="font-semibold">
              {year}
            </span>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11 min-w-11 p-2"
              aria-label={t('monthPicker.nextYear')}
              disabled={year >= 9999}
              onClick={() => setYear(year + 1)}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {Array.from({ length: 12 }, (_, month) => {
              const candidate = dateOf(null);
              candidate.setFullYear(year, month, 1);
              const next = valueOf(candidate);
              return (
                <Button
                  key={month}
                  type="button"
                  variant={value === next ? 'outline' : 'ghost'}
                  className="min-h-11 min-w-11 px-1 text-xs"
                  aria-pressed={value === next}
                  onClick={() => {
                    onChange(next);
                    close();
                  }}
                >
                  {monthName.format(candidate)}
                </Button>
              );
            })}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="mt-1 min-h-11 w-full"
            aria-pressed={all}
            onClick={() => {
              onChange(null);
              close();
            }}
          >
            {t('monthPicker.allPeriods')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
